import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import HandvaerkerfradragBox from '../components/HandvaerkerfradragBox';
import WebContactForm from '../components/WebContactForm';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import { SITE_INFO, BRAND_ASSETS } from '../data';

export default function EntreprenorPage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const contractorImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-liebherr-gravemaskine.webp",
      alt: "Liebherr gravemaskine på KTS-Entreprise arbejdsplads",
      caption: "Liebherr gravemaskine på KTS-Entreprise arbejdsplads"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-indkoersel-fliser.webp",
      alt: "Færdiganlagt indkørsel med flisebelægning",
      caption: "Færdiganlagt indkørsel med flisebelægning"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-brolaegning.webp",
      alt: "Detalje af udført brolægning ved bolig",
      caption: "Detalje af udført brolægning ved bolig"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-mosaik-belaegning.webp",
      alt: "Færdig mosaikbelægning ved bolig",
      caption: "Færdig mosaikbelægning ved bolig"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-belaegningsarbejde.webp",
      alt: "Maskinelt belægningsarbejde udført af KTS-Entreprise",
      caption: "Maskinelt belægningsarbejde udført af KTS-Entreprise"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-grundvandssaenkning.webp",
      alt: "Grundvandssænkning med sugespidsanlæg på byggeplads",
      caption: "Grundvandssænkning med sugespidsanlæg på byggeplads"
    }
  ];
  const tasks = [
    {
      title: 'Jordarbejde og byggemodning',
      desc: 'Udgravning, terrænregulering og klargøring af grunde til byggeri.',
      icon: 'fa-trowel'
    },
    {
      title: 'Belægning og brolægning',
      desc: 'Indkørsler, terrasser, stier og gårdarealer samt belægning med fliser, chaussésten, brosten og kantsten.',
      icon: 'fa-cube'
    },
    {
      title: 'Haveanlæg og gårdrenovering',
      desc: 'Etablering og omlægning af haver, grønne områder og gårdmiljøer.',
      icon: 'fa-tree'
    },
    {
      title: 'Dræn og fugtsikring',
      desc: 'Omfangsdræn, terrændræn og fugtisolering af sokler, kældervægge og fundamenter.',
      icon: 'fa-water'
    },
    {
      title: 'Betonarbejde',
      desc: 'Fundamenter, terrændæk, støttemure, kældre, forskalling og armering udført med høj faglig præcision.',
      link: '/betonarbejde',
      linkText: 'Læs mere om vores betonarbejde',
      icon: 'fa-cubes-stacked'
    }
  ];

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Entreprenør og anlæg | Nordsjælland | KTS-Entreprise A/S"
        description="KTS-Entreprise udfører entreprenør- og anlægsarbejde for private, erhverv, boligforeninger og offentlige bygherrer i Nordsjælland og omegn."
        ogTitle="Entreprenør og anlæg | KTS-Entreprise A/S"
        ogDescription="KTS-Entreprise udfører entreprenør- og anlægsarbejde for private, erhverv, boligforeninger og offentlige bygherrer i Nordsjælland og omegn."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="entreprenerservice"
      />

      {/* Hero Header */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Fagområde</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Entreprenør og anlæg
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise udfører entreprenør- og anlægsarbejde for private, erhverv, boligforeninger og offentlige bygherrer i Nordsjælland og omegn.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Intro & Ydelser */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-7 space-y-6">
            <div className="border-l-4 border-[#0072c0] pl-4">
              <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
                Ydelser inden for entreprenør- og anlægsarbejde
              </h2>
            </div>

            {/* Service Cards */}
            <div className="space-y-4">
              {tasks.map((task) => (
                <div
                  key={task.title}
                  className="bg-white border border-stone-200 p-5 rounded-xs shadow-xs"
                >
                  <div className="flex items-center gap-3 text-[#0072c0] mb-1.5">
                    <i className={`fa-solid ${task.icon} text-lg`} aria-hidden="true"></i>
                    <h3 className="font-heading font-bold text-stone-900 text-base uppercase">
                      {task.title}
                    </h3>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {task.desc}
                  </p>
                  {task.link && (
                    <div className="mt-3 pt-2 border-t border-stone-100">
                      <Link
                        to={task.link}
                        className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase text-[#0072c0] hover:text-[#005b99]"
                      >
                        <span>{task.linkText}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Quick Contact Prompt */}
            <div className="bg-stone-900 text-white p-6 rounded-xs border border-stone-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-heading font-bold text-base uppercase text-white">
                  Kontakt os for et uforpligtende tilbud
                </p>
                <p className="text-stone-400 text-xs mt-0.5">
                  Vi gennemgår gerne opgaven og giver en professionel vurdering.
                </p>
              </div>
              <a
                href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                className="bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-5 py-2.5 text-xs transition-all whitespace-nowrap font-mono rounded-xs"
              >
                <i className="fa-solid fa-phone mr-2"></i>
                {SITE_INFO.mainPhone}
              </a>
            </div>
          </div>

          {/* Right Column: Imagery */}
          <div className="lg:col-span-5 space-y-4">
            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs">
              <ClickableWorkImage
                src={contractorImages[0].src}
                alt={contractorImages[0].alt}
                className="w-full h-auto max-h-[420px] object-cover mx-auto rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 0 })}
              />
            </div>

            <div className="bg-white border border-stone-200 p-5 rounded-xs shadow-xs space-y-2 text-xs">
              <span className="font-heading font-bold uppercase text-stone-900 block text-sm">
                Moderne maskinpark &amp; faglig ekspertise
              </span>
              <p className="text-stone-600 leading-relaxed">
                Vi råder over egne gravemaskiner, specialudstyr og dygtige fagfolk til alle typer jord-, anlægs- og belægningsarbejde i Nordsjælland.
              </p>
            </div>
          </div>
        </div>

        {/* Billeder fra vores entreprenør- og anlægsarbejde */}
        <section className="mb-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
              Billeder fra vores entreprenør- og anlægsarbejde
            </h2>
            <p className="text-stone-600 text-xs sm:text-sm mt-1">
              Glimt fra udførte indkørsler, flisebelægning, brolægning og maskinelt anlægsarbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Indkørsel */}
            <div className="bg-white border border-stone-200 p-2.5 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${contractorImages[1].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={contractorImages[1].alt}
                  className="w-full h-48 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 1 })}
                />
              </div>
              <div className="pt-2.5 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Belægning
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Færdiganlagt indkørsel
                </p>
              </div>
            </div>

            {/* 2. Brolægning */}
            <div className="bg-white border border-stone-200 p-2.5 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${contractorImages[2].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={contractorImages[2].alt}
                  className="w-full h-48 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 2 })}
                />
              </div>
              <div className="pt-2.5 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Brolægning
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Udført brolægning ved bolig
                </p>
              </div>
            </div>

            {/* 3. Mosaik */}
            <div className="bg-white border border-stone-200 p-2.5 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={contractorImages[3].src}
                  alt={contractorImages[3].alt}
                  className="w-full h-48 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 3 })}
                />
              </div>
              <div className="pt-2.5 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Mosaikbelægning
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Færdig mosaikbelægning
                </p>
              </div>
            </div>

            {/* 4. Maskinelt belægningsarbejde */}
            <div className="bg-white border border-stone-200 p-2.5 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={contractorImages[4].src}
                  alt={contractorImages[4].alt}
                  className="w-full h-48 object-cover rounded-xs"
                  style={{ objectPosition: 'center 35%' }}
                  onClick={() => setLightboxState({ isOpen: true, index: 4 })}
                />
              </div>
              <div className="pt-2.5 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Maskinelt arbejde
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Maskinelt belægningsarbejde
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 26. NYT AFSNIT: Grundvandssænkning med sugespidsanlæg */}
        <section id="sugespidsanlaeg" className="bg-white border border-stone-200 p-6 sm:p-10 rounded-xs shadow-xs mb-16 scroll-mt-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-4">
              <div className="border-l-4 border-[#0072c0] pl-4">
                <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#0072c0] block mb-1">
                  Specialudstyr
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
                  Grundvandssænkning med sugespidsanlæg
                </h2>
              </div>

              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Ved udgravninger med højtstående grundvand kan det være nødvendigt midlertidigt at sænke grundvandsstanden, så arbejdet kan udføres sikkert og under stabile forhold.
              </p>

              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                KTS-Entreprise råder over eget sugespidsanlæg til grundvandssænkning. Anlægget består af sugespidser, som placeres omkring udgravningen og forbindes til en pumpe. På den måde sænkes grundvandet lokalt, så tilstrømmende vand begrænses, og udgravningen kan holdes tør under arbejdet.
              </p>

              <div className="bg-stone-50 border border-stone-200 p-5 rounded-xs space-y-2 mt-4">
                <h3 className="font-heading font-bold uppercase text-stone-900 text-sm">
                  Sugespidsanlæg kan blandt andet anvendes i forbindelse med:
                </h3>
                <ul className="space-y-1.5 text-xs sm:text-sm text-stone-700 list-disc pl-4">
                  <li>Udgravning til kældre og fundamenter</li>
                  <li>Betonarbejde under grundvandsniveau</li>
                  <li>Etablering og reparation af kloakledninger</li>
                  <li>Montering af brønde og tanke</li>
                  <li>Drænarbejde</li>
                  <li>Andre dybe udgravninger med tilstrømmende grundvand</li>
                </ul>
              </div>

              <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-medium">
                Løsningen planlægges ud fra jordbundsforholdene, grundvandsstanden, udgravningens dybde og de gældende krav til bortledning af det oppumpede vand.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-4">
              <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${contractorImages[5].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={contractorImages[5].alt}
                  className="w-full h-auto object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 5 })}
                />
              </div>

              <div className="bg-slate-50 border border-slate-200 p-4 rounded-xs text-xs text-stone-600">
                <p className="font-semibold text-stone-900 mb-1">
                  Skal du have udført dybe udgravninger?
                </p>
                <p>
                  Vi rådgiver om etablering og drift af sugespidsanlæg tilpasset dit projekt.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 25. Håndværkerfradrag */}
        <HandvaerkerfradragBox />

        {/* Kontaktformular */}
        <div className="mt-16">
          <WebContactForm
            title="Få et uforpligtende tilbud"
            subtitle="Udfyld formularen for et uforpligtende tilbud på entreprenør-, have- eller anlægsarbejde."
            submitText="Send forespørgsel"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Jordarbejde og byggemodning",
              "Belægning og brolægning",
              "Haveanlæg og gårdrenovering",
              "Dræn og fugtsikring",
              "Grundvandssænkning / sugespidsanlæg",
              "Andet entreprenørarbejde"
            ]}
            showFileUpload={true}
            fileLabel="Vedhæft gerne billeder, tegninger eller geoteknisk rapport (valgfrit)"
          />
        </div>

        {/* Lightbox for Entreprenørarbejde */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={contractorImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Entreprenør- og anlægsarbejde"
        />

      </div>
    </div>
  );
}
